export interface TaskQL {
  id: string;
  version: number;
  name: string;
  contactName: string;
  ref: string;
  providerId: string;
  policyNumber: string;
  riskCode: string;
  partyCode: string;
  email: string;
  emails: null;
  deviceType?: Task.DeviceTypeEnum;
  tel: string;
  notify: boolean;
  companyId: string;
  userId: string;
  createdDate: number;
  assignedDate: number;
  completedDate: number;
  resolution: null;
  status: Task.StatusEnum;
  type: string;
  contract: string;
  contractReference: ContractReference;
  service: string;
  cancellationReason: null;
  claimed: null;
  techValidator: null;
  techValidation_ts: null;
  claimValidator: null;
  claimValidation_ts: null;
  address: AddressLocation;
  notes: NotesItem[];
  install: Install;
  questionnaire: QuestionnaireItem[];
  companyReference: CompanyReference;
  providerReference: ProviderReference;
  schedulerMeta: {
    expiredTime: number,
    acceptedSchedulerSnn: string,
  }

  hasDisabledDiagnosticTests: boolean;
}
interface Address {
  detail: Detail;
  geometry: Geometry;
  building: String;
  streetAddress: string;
}

export interface AddressLocation {
  latitude: number;
  longitude: number;
  point?: any[];
  building?: string;
  streetAddress?: string;
  locality?: string;
  municipality?: string;
  province?: string;
  state?: string;
  country?: string;
}

interface Detail {
  address: string;
  street: string;
  suburb: string;
  city: string;
  postalcode: string;
}
interface Geometry {
  location: Location;
}
interface Location {
  lat: number;
  lng: number;
}
interface NotesItem {
  timestamp: number;
  note: string;
  userId: string;
}
interface Install {
  tags: null;
  geyserId: null;
  oldSerial: null;
  newSerial: null;
  serial: string;
  activationPassword?: string;
  simimsi: null;
  images: {
    src: string;
    tag: string;
    comment?: string;
    label?: string;
  };
  customerFeedback: null;
  questionsAnswers: QuestionsAnswersItem[];
}
interface QuestionsAnswersItem {
  question: string;
  answer: string;
}
interface QuestionnaireItem {
  label: string;
  tag: string;
  type: string;
  default: string;
  child: string;
  options: {
    title: string;
    value: string | number;
  };
}
interface CompanyReference {
  snn: string;
  name: string;
  tags: string[];
  created_ts: number;
  meta: Meta;
}
interface Meta {
  'geyser:contact': {
    phoneNumber: string;
    onboardingUrl: string;
  };
  'sensordesk:info'?: {
    color: string;
  };
}
interface ProviderReference {
  snn: string;
  name: string;
  tags: string[];
  created_ts: number;
  meta: Meta;
}

// Basic Task Object

export interface Task {
  tags?: Array<string>;
  id?: string;
  ownerName: string;
  contactName: string;
  ref?: string;
  providerId?: string;
  policyNumber?: string;
  postponedReason?: string;
  postponed_ts?: number;
  riskCode?: string;
  partyCode?: string;
  email: string;
  emails?: Array<string>;
  tel: string;
  tels?: Array<string>;
  address?: TaskAddress;
  detail?: string;
  notify?: boolean;
  deviceType?: Task.DeviceTypeEnum;
  companyId: string;
  userId?: string;
  createdDate: number;
  assignedDate?: number;
  completedDate?: number;
  status: Task.StatusEnum;
  type: string;
  notes?: Array<TaskNote>;
  install?: TaskInstall;
  cancellationReason?: string;
  claimed?: boolean;
  contract?: string;
  resolution?: string;
  requiredImages?: {
    comment?: string;
    label?: string;
    tag?: string;
    src?: string;
  };
  schedulerMeta: {
    expiredTime?: number;
    acceptedSchedulerSnn?: string;
  }
  skippedTests?: boolean;
  schedulerRejectingTask?: boolean;
}

export interface TaskInstall {
  tags?: Array<string>;
  activationPassword?: string;
  geyserId?: string;
  'old-serial'?: string;
  serial?: string;
  simimsi?: string;
  images?: Array<string> | any;
  customerFeedback?: string;
  questionAnswers?: Array<QuestionAnswers>;
  questionsAnswers?: Array<QuestionAnswers>;
}

export interface QuestionAnswers {
  question: string;
  answer?: string;
}

export namespace Task {
  export enum StatusEnum {
    Unscheduled = 'unscheduled',
    Scheduled = 'scheduled',
    Pending = 'pending',
    Inprogress = 'inprogress',
    Closed = 'closed',
    ToReschedule = 'to-reschedule',
    Deleted = 'deleted',
    PendingTechinicalValidation = 'pending-tech-validation',
    PostponedTechinicalValidation = 'postponed-tech-validation',
    PendingClaimValidation = 'pending-claim-validation'
  }
  export enum DeviceTypeEnum {
    Geyser = 'geyser',
    Congey = 'congey',
    Security = 'secdev',
    FireAlarm = 'firealarm'
  }
}

export interface TaskNote {
  timestamp?: number;
  note?: string;
  userId?: string;
}

export interface TaskAddress {
  addressComponents?: Array<AddressAddressComponents>;
  formattedAddress?: string;
  geometry?: AddressGeometry;
  placeId?: string;
  types?: Array<string>;
  detail?: any;
  building?: string;
  latitude?: number;
  longitude?: number;
}

export interface AddressAddressComponents {
  longName?: string;
  shortName?: string;
  types?: Array<string>;
}
export interface AddressGeometry {
  location?: AddressGeometryLocation;
  locationType?: string;
  viewport?: AddressGeometryViewport;
}

export interface AddressGeometryLocation {
  lat: number;
  lng: number;
}
export interface AddressGeometryViewport {
  south?: number;
  west?: number;
  north?: number;
  east?: number;
}
export const deviceTypes: { value: Task.DeviceTypeEnum; label: string }[] = [
  {
    value: Task.DeviceTypeEnum.Security,
    label: 'Security Device'
  },
  {
    value: Task.DeviceTypeEnum.FireAlarm,
    label: 'Fire Alarm'
  },
  {
    value: Task.DeviceTypeEnum.Geyser,
    label: 'Geyser Controller'
  },
  {
    value: Task.DeviceTypeEnum.Congey,
    label: 'Connected Geyser Controller'
  }
];

export interface TaskData {
  // TODO: need all the info code etc
  photos?: any[];
  comments?: any[];
  questions?: any[];
  feedback?: string;
  test?: boolean;
  code?: string;
  deviceReplace?: boolean;
  newSerial?: string;
  verified?: string;
  testrepair?: any;
  skippedTests?: boolean;
}

export interface ImageObject {
  label?: string;
  comment?: string;
  src?: string;
  tag?: string;
  data?: string;
}
export interface FileContents {
  contentType: string;
  content: Uint8Array | string;
}
export interface ContractReference {
  snn: string;
  ref: string;
  version: number;
  created_ts: number;
  started_ts: null;
  finished_ts: null;
  closed_ts: null;
  state: string;
  type: string;
  provider: string;
  lastCompany: string;
  name: string;
  policyNumber: string;
  riskCode: null;
  partyCode: null;
  phoneNumber: string;
  emailAddress: string;
  notes: ContractNote[];
}
export interface ContractNote {
  snn: string;
  created_ts: number;
  deleted_ts: number | null;
  creator: string;
  text: string;
  service: string | null;
}



// WEBPACK FOOTER //
// ./src/providers/tasks/models.ts